import React from "react";
import { Check, X, ShoppingBag, CircleAlert, Info } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
const getField = (post: any, key: string, fallback: any = null) => {
  return post.customFields?.[key] ?? fallback;
};
function ReviewsListConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="space-y-12">
        {[...Array(2)].map((_, i) => <div key={i} className="grid grid-cols-1 gap-8 lg:grid-cols-3 animate-pulse" data-index={i}>
            <div className="space-y-4 lg:col-span-2">
              <div className="h-8 w-3/4 rounded bg-muted/20" />
              <div className="h-32 w-full rounded bg-muted/10" />
              <div className="h-48 w-full rounded bg-muted/10" />
            </div>
            <div className="h-96 w-full rounded bg-muted/10 lg:col-span-1" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center text-destructive">
        <p>Unable to load reviews at this time.</p>
        <Button variant="outline" onClick={refetch} className="mt-4 border-destructive/50 hover:bg-destructive/20">
          Try Again
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="py-12 text-center text-muted-foreground">
        <p>No reviews found in the wild yet.</p>
      </div>;
  }
  return <div className="space-y-16">
      {posts.map((post, i) => {
      const verdict = getField(post, "verdict", "This gear has been field-tested thoroughly. It stands up to the rugged demands of the American West, though users should be mindful of specific limitations.");
      const verdictType = getField(post, "verdict_type", i % 2 === 0 ? "recommend" : "caution");
      const pros = getField(post, "pros", ["Durable construction", "Lightweight design", "Easy to deploy"]);
      const cons = getField(post, "cons", ["Premium price point", "Limited color options"]);
      const specs = getField(post, "specs", {
        "Weight": "2.5 lbs",
        "Dimensions": "12x8x4 in",
        "Material": "Ripstop Nylon",
        "Warranty": "Lifetime"
      });
      const retailerLink = getField(post, "retailer_link", "#");
      const retailerName = getField(post, "retailer_name", "Official Retailer");
      const isRecommend = verdictType === "recommend";
      return <article key={post.id} data-index={i} className="grid grid-cols-1 gap-8 border-b border-border pb-16 last:border-0 lg:grid-cols-12">
            {}
            <div className="space-y-8 lg:col-span-8">
              <header className="space-y-4">
                <h3 className="font-serif text-4xl font-bold tracking-wide text-foreground">
                  {post.title}
                </h3>
                <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                  <span>Tested in {getField(post, "location", "Utah Backcountry")}</span>
                  <span>•</span>
                  <span>{new Date(post.date || "").toLocaleDateString()}</span>
                </div>
              </header>

              {}
              <Alert className={`border-l-4 ${isRecommend ? "border-accent bg-accent/5 text-foreground" : "border-primary bg-primary/5 text-foreground"}`}>
                {isRecommend ? <Check className="h-5 w-5 text-accent" /> : <CircleAlert className="h-5 w-5 text-primary" />}
                <AlertTitle className={`mb-2 text-lg font-bold ${isRecommend ? "text-accent" : "text-primary"}`}>
                  The Verdict: {isRecommend ? "Recommended" : "Proceed with Caution"}
                </AlertTitle>
                <AlertDescription className="text-base leading-relaxed opacity-90">
                  {verdict}
                </AlertDescription>
              </Alert>

              {}
              <div className="prose prose-invert max-w-none text-muted-foreground" dangerouslySetInnerHTML={{
            __html: post.content || post.excerpt || ""
          }} />

              {}
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="rounded-lg border border-border bg-card/50 p-6">
                  <h4 className="mb-4 flex items-center gap-2 font-bold text-accent">
                    <Check className="h-5 w-5" /> The Good
                  </h4>
                  <ul className="space-y-3">
                    {Array.isArray(pros) && pros.map((pro: string, idx: number) => <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent/50" />
                        {pro}
                      </li>)}
                  </ul>
                </div>

                <div className="rounded-lg border border-border bg-card/50 p-6">
                  <h4 className="mb-4 flex items-center gap-2 font-bold text-primary">
                    <X className="h-5 w-5" /> The Bad
                  </h4>
                  <ul className="space-y-3">
                    {Array.isArray(cons) && cons.map((con: string, idx: number) => <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/50" />
                        {con}
                      </li>)}
                  </ul>
                </div>
              </div>

              {}
              <div className="block lg:hidden">
                <Link to={retailerLink} newTab className="w-full">
                  <Button size="lg" className="w-full text-lg font-bold shadow-lg shadow-primary/20">
                    <ShoppingBag className="mr-2 h-5 w-5" />
                    Shop at {retailerName}
                  </Button>
                </Link>
              </div>
            </div>

            {}
            <aside className="lg:col-span-4">
              <Card className="sticky top-24 overflow-hidden border-border bg-card/30 backdrop-blur-sm">
                <CardHeader className="border-b border-border bg-muted/20 pb-4">
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <Info className="h-5 w-5 text-muted-foreground" />
                    Tech Specs
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-border">
                    {Object.entries(specs).map(([key, value], idx) => <div key={idx} className="grid grid-cols-2 gap-4 p-4 text-sm">
                        <dt className="font-medium text-muted-foreground">{key}</dt>
                        <dd className="text-right font-semibold text-foreground">{String(value)}</dd>
                      </div>)}
                  </div>
                  <div className="bg-muted/10 p-6">
                    <Link to={retailerLink} newTab className="block w-full">
                      <Button size="lg" className="w-full text-lg font-bold shadow-lg shadow-primary/20">
                        <ShoppingBag className="mr-2 h-5 w-5" />
                        Shop Now
                      </Button>
                    </Link>
                    <p className="mt-3 text-center text-xs text-muted-foreground opacity-60">
                      *Affiliate links support our adventures.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </aside>
          </article>;
    })}
    </div>;
}
export default function TestedInTheWild() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    orderby: "date",
    order: "DESC"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="tested-in-the-wild" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03] bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562326/img/topo_pattern_dark.png)] bg-repeat bg-[length:400px_400px]" />
      
      <div className="container relative z-10 mx-auto px-4">
        <div className="mb-16 max-w-3xl">
          <h2 className="font-serif text-5xl font-bold tracking-tight text-foreground sm:text-6xl">
            Tested in the Wild
          </h2>
          <p className="mt-6 text-xl text-muted-foreground">
            Real-world gear reviews from the dusty trails of the American West. 
            We put equipment through the wringer so you don't have to guess.
          </p>
        </div>

        <PostsProvider wp_query={wp_query}>
          <ReviewsListConsumer />
        </PostsProvider>
      </div>
    </section>;
}